import React, { useState } from "react";
import * as XLSX from "xlsx";
import "../style/UploadExcel.css";
import { useSelector } from "react-redux";

const UploadExcel = () => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState(null);
  const [preview, setPreview] = useState([]);
  const [uploadCompleted, setUploadCompleted] = useState(false);
  const user = useSelector((state) => state.user?.user);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (
      selectedFile &&
      selectedFile.type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    ) {
      setFile(selectedFile);
      parseExcelPreview(selectedFile);
    } else {
      alert("Please select a valid Excel file (.xlsx)");
    }
  };

  const parseExcelPreview = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];

      // Try reading without headers first to see the raw structure.
      const rawData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

      // Find the header row (look for a row that contains expected column names).
      let headerRowIndex = -1;
      for (let i = 0; i < Math.min(10, rawData.length); i++) {
        const row = rawData[i];
        if (row && Array.isArray(row)) {
          // Check if this row contains header-like values.
          const hasCountry = row.some(
            (cell) => cell && cell.toString().toLowerCase().includes("country"),
          );
          const hasDistillery = row.some(
            (cell) =>
              cell && cell.toString().toLowerCase().includes("distillery"),
          );
          const hasName = row.some(
            (cell) => cell && cell.toString().toLowerCase().includes("name"),
          );

          if (hasCountry || hasDistillery || hasName) {
            headerRowIndex = i;
            break;
          }
        }
      }

      let jsonData;
      if (headerRowIndex >= 0) {
        // Use the found header row
        const range = XLSX.utils.decode_range(worksheet["!ref"]);
        range.s.r = headerRowIndex; // Start from header row
        worksheet["!ref"] = XLSX.utils.encode_range(range);
        jsonData = XLSX.utils.sheet_to_json(worksheet);
      } else {
        // Fallback: assume standard structure and use manual headers.
        jsonData = rawData
          .slice(1)
          .map((row) => ({
            Country: row[0] || "",
            Distillery: row[1] || "",
            Name: row[2] || "",
            "%": row[3] || "",
            "€/2cl": row[4] || "",
          }))
          .filter((row) => row.Name); // Only include rows with names.
      }

      const transformedData = jsonData
        .filter((row) => {
          if (
            row["Country"] === "Country" ||
            row["Distillery"] === "Distillery"
          )
            return false;
          if (
            row["Distillery"] === "Päivitetty" ||
            row["Country"] === "Päivitetty"
          )
            return false;
          if (!row["Country"] && !row["Distillery"]) return false;
          return row["Name"] && row["Name"].trim().length > 0;
        })
        .slice(0, 10)
        .map((row) => ({
          whiskey_title: row["Name"] || "",
          distillery: row["Distillery"] ? [row["Distillery"]] : [""],
          country: row["Country"] || "",
          alc: parseFloat(row["%"]) || 0,
          price: parseFloat(row["€/2cl"]) || 0,
          // TODO Remove later if not used.
          // is_in_stock: row["In Stock"] !== "No" && row["In Stock"] !== "false",
        }));

      setPreview(transformedData);
    };
    reader.readAsArrayBuffer(file);
  };

  const uploadWhiskies = async () => {
    if (!file) return;

    setUploading(true);

    try {
      // Parse Excel file.
      const reader = new FileReader();
      reader.onload = async (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        // Try reading without headers first to see the raw structure.
        const rawData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        // Find the header row (look for a row that contains expected column names).
        let headerRowIndex = -1;
        for (let i = 0; i < Math.min(10, rawData.length); i++) {
          const row = rawData[i];
          if (row && Array.isArray(row)) {
            // Check if this row contains header-like values
            const hasCountry = row.some(
              (cell) =>
                cell && cell.toString().toLowerCase().includes("country"),
            );
            const hasDistillery = row.some(
              (cell) =>
                cell && cell.toString().toLowerCase().includes("distillery"),
            );
            const hasName = row.some(
              (cell) => cell && cell.toString().toLowerCase().includes("name"),
            );

            if (hasCountry || hasDistillery || hasName) {
              headerRowIndex = i;
              break;
            }
          }
        }

        let jsonData;
        if (headerRowIndex >= 0) {
          // Use the found header row
          const range = XLSX.utils.decode_range(worksheet["!ref"]);
          range.s.r = headerRowIndex; // Start from header row
          worksheet["!ref"] = XLSX.utils.encode_range(range);
          jsonData = XLSX.utils.sheet_to_json(worksheet);
        } else {
          // Fallback: assume standard structure and use manual mapping
          jsonData = rawData
            .slice(1)
            .map((row) => ({
              Country: row[0] || "",
              Distillery: row[1] || "",
              Name: row[2] || "",
              "%": row[3] || "",
              "€/2cl": row[4] || "",
            }))
            .filter((row) => row.Name); // Only include rows with names.
        }

        // Helper function to find column name variations.
        const findColumn = (data, possibleNames) => {
          if (!data || data.length === 0) return null;
          const columns = Object.keys(data[0]);
          return possibleNames.find((name) =>
            columns.some(
              (col) => col.toLowerCase().trim() === name.toLowerCase(),
            ),
          );
        };

        // Find actual column names in the Excel file
        const nameCol = findColumn(jsonData, ["Name", "name"]);
        const countryCol = findColumn(jsonData, ["Country", "country"]);
        const distilleryCol = findColumn(jsonData, [
          "Distillery",
          "distillery",
        ]);
        const alcCol = findColumn(jsonData, ["%", "Alc", "Alcohol", "alc"]);
        const priceCol = findColumn(jsonData, ["€/2cl", "€", "Price", "price"]);

        // Helper function to check if a row is a header row.
        const isHeaderRow = (row, nameCol, countryCol, distilleryCol) => {
          if (!row) return false;

          // Check if any of the expected columns literally repeat their own column name
          // (exact match, not substring - a distillery legitimately named e.g.
          // "Kyrö Distillery" must not be mistaken for a repeated header row).
          const nameIsHeader =
            nameCol &&
            row[nameCol] &&
            row[nameCol].toString().trim().toLowerCase() === "name";
          const countryIsHeader =
            countryCol &&
            row[countryCol] &&
            row[countryCol].toString().trim().toLowerCase() === "country";
          const distilleryIsHeader =
            distilleryCol &&
            row[distilleryCol] &&
            row[distilleryCol].toString().trim().toLowerCase() === "distillery";

          return nameIsHeader || countryIsHeader || distilleryIsHeader;
        };

        // Transform to Supabase format.
        const whiskies = jsonData
          .filter((row) => {
            // Skip repeated header rows anywhere in the file.
            if (isHeaderRow(row, nameCol, countryCol, distilleryCol)) {
              return false;
            }

            // Skip "Päivitetty" row (Finnish for "Updated").
            if (
              (distilleryCol && row[distilleryCol] === "Päivitetty") ||
              (countryCol && row[countryCol] === "Päivitetty")
            ) {
              return false;
            }

            // Skip rows where Country and Distillery are both empty.
            if (
              (!countryCol || !row[countryCol]) &&
              (!distilleryCol || !row[distilleryCol])
            ) {
              return false;
            }

            // Only include rows that have a whiskey name.
            const hasName =
              nameCol &&
              row[nameCol] &&
              row[nameCol].toString().trim().length > 0;
            return hasName;
          })
          .map((row) => ({
            whiskey_title: nameCol ? row[nameCol] || "" : "",
            distillery:
              distilleryCol && row[distilleryCol] ? [row[distilleryCol]] : [""],
            country: countryCol ? row[countryCol] || "" : "",
            alc: alcCol ? parseFloat(row[alcCol]) || 0 : 0,
            price: priceCol ? parseFloat(row[priceCol]) || 0 : 0,

            // TODO Remove later if not used.
            // cask: row["Cask"] ? [row["Cask"]] : [""],
            // region: row["Region"] || "",
            // description: row["Description"] || "",
            // tags: row["Tags"]
            //   ? row["Tags"].split(",").map((tag) => tag.trim())
            //   : [],
            // is_in_stock:
            //   row["In Stock"] !== "No" && row["In Stock"] !== "false",

            // Set defaults for fields not in your Excel.
            region: "",
            cask: [""],
            description: "",
            tags: [],
            is_in_stock: true,
          }));

        if (whiskies.length === 0) {
          throw new Error(
            "No valid whiskey data found in the Excel file. Please check the column names and data format.",
          );
        }

        // Check if there are existing whiskies and delete them if any exist.
        const checkResponse = await fetch(
          `${process.env.REACT_APP_SUPABASE_URL}/rest/v1/whiskies?select=id&limit=1`,
          {
            headers: {
              apikey: process.env.REACT_APP_SUPABASE_ANON_KEY,
              Authorization: `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
              "x-api-key": process.env.REACT_APP_API_KEY,
            },
          },
        );

        if (!checkResponse.ok) {
          const checkError = await checkResponse.text();
          console.error("Check existing whiskies failed:", checkError);
          throw new Error(
            `Failed to check existing whiskies: ${checkResponse.status} ${checkResponse.statusText}`,
          );
        }

        const existingWhiskies = await checkResponse.json();

        // Only delete if there are existing whiskies.
        if (existingWhiskies && existingWhiskies.length > 0) {
          const deleteResponse = await fetch(
            `${process.env.REACT_APP_SUPABASE_URL}/rest/v1/whiskies?id=gt.0`,
            {
              method: "DELETE",
              headers: {
                apikey: process.env.REACT_APP_SUPABASE_ANON_KEY,
                Authorization: `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
                "Content-Type": "application/json",
                "x-api-key": process.env.REACT_APP_API_KEY,
              },
            },
          );

          if (!deleteResponse.ok) {
            const deleteError = await deleteResponse.text();
            console.error("Delete failed:", deleteError);
            throw new Error(
              `Failed to delete existing whiskies: ${deleteResponse.status} ${deleteResponse.statusText}. ${deleteError}`,
            );
          }
        }

        // Insert new whiskies.
        const response = await fetch(
          `${process.env.REACT_APP_SUPABASE_URL}/rest/v1/whiskies`,
          {
            method: "POST",
            headers: {
              apikey: process.env.REACT_APP_SUPABASE_ANON_KEY,
              Authorization: `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
              "Content-Type": "application/json",
              Prefer: "return=minimal",
              "x-api-key": process.env.REACT_APP_API_KEY,
            },
            body: JSON.stringify(whiskies),
          },
        );

        if (response.ok) {
          const isReplacement = existingWhiskies && existingWhiskies.length > 0;
          setResults({
            success: true,
            message: isReplacement
              ? `Successfully replaced all whiskies with ${whiskies.length} new entries.`
              : `Successfully added ${whiskies.length} whiskies to the empty database.`,
            count: whiskies.length,
          });
          setUploadCompleted(true);
        } else {
          const error = await response.text();
          setResults({ success: false, error });
        }

        setUploading(false);
      };

      reader.readAsArrayBuffer(file);
    } catch (error) {
      setResults({ success: false, error: error.message });
      setUploading(false);
    }
  };

  if (user) {
    return (
      <div className="excel-view">
        <h2>Upload Whiskies from Excel</h2>

        <div className="file-input-section">
          <input
            className="upload-excel"
            type="file"
            accept=".xlsx"
            onChange={handleFileChange}
          />
          <p className="warning-text">
            ⚠️ This will replace ALL existing whiskies with the data from your
            Excel file
          </p>
        </div>

        {preview.length > 0 && (
          <div className="preview-section">
            <h3>Preview (first 10 rows):</h3>
            <div className="table-container">
              <table className="preview-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Distillery</th>
                    <th>Country</th>
                    <th>%</th>
                    <th>€/2cl</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.map((whiskey, index) => (
                    <tr key={index}>
                      <td>{whiskey.whiskey_title}</td>
                      <td>{whiskey.distillery[0]}</td>
                      <td>{whiskey.country}</td>
                      <td>{whiskey.alc}</td>
                      <td>{whiskey.price}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {file && (
          <button
            className={`upload-button ${uploading || uploadCompleted ? "disabled" : ""}`}
            onClick={uploadWhiskies}
            disabled={uploading || uploadCompleted}
          >
            {uploading
              ? "Uploading..."
              : uploadCompleted
                ? "Upload Completed!"
                : "Replace all whiskies"}
          </button>
        )}

        {results && (
          <div className={`results ${results.success ? "success" : "error"}`}>
            {results.success ? (
              <div>
                <h4>Upload Successful!</h4>
                <p>{results.message}</p>
              </div>
            ) : (
              <div>
                <h4>Upload Failed</h4>
                <p>{results.error}</p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
};

export default UploadExcel;
